if (!customElements.get('mc-collection-deck')) {
  customElements.define('mc-collection-deck', class extends HTMLElement {
    connectedCallback() {
      this.dataset.js = '';
      this.dialog = this.querySelector('dialog');
      this.deck = this.querySelector('[data-deck]');
      this.sheet = this.querySelector('[data-sheet]');
      this.items = [...this.querySelectorAll('.mcd-item')];
      this.layers = [...this.querySelectorAll('[data-ambient]')];
      this.activeLayer = 0;
      this.liteQuery = matchMedia('(max-width: 740px), (hover: none)');
      this.ac = new AbortController();
      const on = { signal: this.ac.signal };

      const syncLite = () => { this.toggleAttribute('data-lite', this.liteQuery.matches); };
      syncLite();
      this.liteQuery.addEventListener('change', syncLite, on);

      this.deck.addEventListener('click', (e) => {
        const card = e.target.closest('.mcd-deck__card');
        if (card && this.isPlainClick(e)) { e.preventDefault(); this.open(card); }
      }, on);
      this.querySelector('[data-open]').addEventListener('click', (e) => {
        if (this.isPlainClick(e)) { e.preventDefault(); this.open(null); }
      }, on);
      this.querySelector('[data-close]').addEventListener('click', () => this.close(), on);
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, on);
      // El navegador puede cerrar el diálogo sin pasar por close() (p. ej. gesto de atrás en Android).
      this.dialog.addEventListener('close', () => {
        this.dialog.classList.remove('is-open');
        this.items.forEach((item) => item.getAnimations().forEach((anim) => anim.cancel()));
        this.unlock();
        this.closing = false;
        this.openAnimations = null;
      }, on);
      this.sheet.addEventListener('click', (e) => { if (!e.target.closest('a')) this.close(); }, on);

      const ambientFrom = (e) => {
        const card = e.target.closest('.mcd-card');
        if (card) this.setAmbient(card.dataset.ambientSrc);
      };
      this.sheet.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse') ambientFrom(e); }, on);
      this.sheet.addEventListener('focusin', ambientFrom, on);

      // En el editor de temas, seleccionar un bloque abre la vista para poder revisarlo.
      document.addEventListener('shopify:block:select', (e) => { if (this.contains(e.target)) this.open(null); }, on);
      document.addEventListener('shopify:section:deselect', (e) => { if (e.target.contains(this)) this.close(); }, on);

      // El abanico se abre una sola vez, para que no se reacomode al seguir bajando.
      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-in');
        this.observer.disconnect();
        this.preloadImages();
      }, { threshold: 0.3 });
      this.observer.observe(this.deck);
    }

    disconnectedCallback() {
      this.ac.abort();
      this.observer.disconnect();
      this.unlock();
    }

    preloadImages() {
      const images = [...this.dialog.querySelectorAll('.mcd-card img')];
      images.forEach((img) => { img.loading = 'eager'; });
      images.slice(0, 6).forEach((img) => { if (img.decode) img.decode().catch(() => {}); });
    }

    isPlainClick(e) {
      return !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button);
    }

    get reducedMotion() {
      return matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    lock() {
      const gap = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.classList.add('mcd-lock');
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
    }

    unlock() {
      document.documentElement.classList.remove('mcd-lock');
      document.body.style.paddingRight = '';
    }

    setAmbient(src) {
      if (!src || src === this.ambientSrc) return;
      this.ambientSrc = src;
      const next = this.layers[1 - this.activeLayer];
      next.style.backgroundImage = `url("${src}")`;
      next.classList.add('is-active');
      this.layers[this.activeLayer].classList.remove('is-active');
      this.activeLayer = 1 - this.activeLayer;
    }

    originRect(card) {
      const middle = this.deck.children[Math.floor(this.deck.children.length / 2)];
      return (card || middle || this.deck).getBoundingClientRect();
    }

    offsetFrom(item, origin) {
      const r = item.getBoundingClientRect();
      return {
        x: origin.left + origin.width / 2 - (r.left + r.width / 2),
        y: origin.top + origin.height / 2 - (r.top + r.height / 2),
        s: Math.min(origin.width / r.width, 1.4),
        visible: r.top < window.innerHeight
      };
    }

    open(card) {
      if (this.dialog.open) return;
      const origin = this.originRect(card);
      this.lock();
      this.dialog.showModal();
      this.sheet.scrollTop = 0;

      const target = card && this.dialog.querySelector(`.mcd-card[data-card-id="${card.dataset.cardId}"]`);
      const first = target || this.dialog.querySelector('.mcd-card');
      if (first) this.setAmbient(first.dataset.ambientSrc);
      if (target) target.focus({ preventScroll: true });

      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      if (this.reducedMotion) return;

      if (this.hasAttribute('data-lite')) {
        // Celular: las tarjetas visibles suben en cascada; las de más abajo ya están en su sitio.
        const visible = this.items.slice(0, 6);
        this.openAnimations = visible.map((item, i) => item.animate([
          { transform: 'translateY(26px)', opacity: 0 },
          { transform: 'none', opacity: 1 }
        ], { duration: 460, delay: 140 + i * 55, easing: 'cubic-bezier(.2, .8, .2, 1)', fill: 'backwards' }));
        return;
      }

      const tilt = [-8, 6, -4, 9, -6, 4, -9, 7, -3, 5, -7, 8];
      const animations = this.items.map((item, i) => {
        const { x, y, s } = this.offsetFrom(item, origin);
        return item.animate([
          { transform: `translate(${x}px, ${y}px) rotate(${tilt[i % tilt.length]}deg) scale(${s})`, opacity: 0 },
          { opacity: 1, offset: 0.2 },
          { transform: 'none', opacity: 1 }
        ], { duration: 820, delay: i * 55, easing: 'cubic-bezier(.2, .8, .2, 1)', fill: 'both' });
      });
      this.openAnimations = animations;
      Promise.allSettled(animations.map((a) => a.finished)).then(() => {
        if (this.openAnimations !== animations) return;
        animations.forEach((a) => a.cancel());
        this.openAnimations = null;
      });
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      // Se puede cerrar aunque las tarjetas sigan saliendo.
      if (this.openAnimations) {
        this.openAnimations.forEach((a) => a.cancel());
        this.openAnimations = null;
      }
      this.dialog.classList.remove('is-open');
      const finish = () => this.dialog.close();
      if (this.reducedMotion) { finish(); return; }

      this.closing = true;
      if (this.hasAttribute('data-lite')) {
        this.dialog.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-out', fill: 'forwards' })
          .finished.catch(() => {}).then(() => {
            finish();
            this.dialog.getAnimations().forEach((a) => a.cancel());
          });
        return;
      }

      const origin = this.originRect(null);
      const animations = this.items.map((item, i) => {
        const { x, y, s, visible } = this.offsetFrom(item, origin);
        const end = visible
          ? { transform: `translate(${x}px, ${y}px) rotate(${i % 2 ? 5 : -5}deg) scale(${s})`, opacity: 0 }
          : { opacity: 0 };
        return item.animate([{ transform: 'none', opacity: 1 }, end], {
          duration: 380,
          delay: (this.items.length - 1 - i) * 18,
          easing: 'cubic-bezier(.5, 0, .75, 0)',
          fill: 'both'
        });
      });
      Promise.allSettled(animations.map((a) => a.finished)).then(finish);
    }
  });
}